import React, { useState, useEffect, useRef } from 'react';
import { useCSVReader } from 'react-papaparse';
import { message } from 'antd';
import { CloudUploadOutlined } from '@ant-design/icons';
import { identity } from 'lodash';

import PdButton from 'components/pd-button';

import CustomerService from 'services/customer';
import StockService from 'services/stock';
import RentStockServices from 'services/rent-stock';

import { errHandling } from 'utils';

import './style.scss';

/**
 *
 * @param {() => {}} afterUpload - callback when file is uploaded
 * @param {String} buttonTitle - title of button
 * @param {'customer' | 'stock' | 'rentStock' | ''} purpose - to indicate which BE API that must be hit to update data
 * @param {Boolean} noButton - for style button
 */

export function PdCsvUploader({
  afterUpload = identity,
  buttonTitle = 'Upload .csv',
  header = true,
  purpose = '',
  transformHeader = (header) => header,
  noButton = false,
  type = 'primary',
}) {
  const { CSVReader } = useCSVReader();
  const removeButtonRef = useRef(null);
  const [shouldRemove, setShouldRemove] = useState(false);

  // Programmatically click remove button when shouldRemove is true
  useEffect(() => {
    if (shouldRemove && removeButtonRef.current) {
      removeButtonRef.current.click();
      setShouldRemove(false);
    }
  }, [shouldRemove]);

  const requiredFieldName = {
    customer: [],
    keyword: ['sku', 'keyword'],
    penalties: ['sku', 'title', 'price'],
    rentStock: ['sku', 'quantity'],
    stock: ['sku', 'quantity'],
    bulk_create: [],
    bulk_discount: ['sku', 'warehouse_id'],
  };

  async function handleOnFileLoad(data) {
    const lowerCaseKeys = (obj) =>
      Object.keys(obj).reduce((acc, key = '') => {
        acc[key.toLowerCase()] = obj[key];
        return acc;
      }, {});
    // v4 returns ParseResult with data directly as array of objects (when header: true)
    const arrayOfData = (data.data || []).map((row) => lowerCaseKeys(row));
    let fieldName = '';
    let row = '';

    // iteration for validation
    for (let i = 0; i < arrayOfData.length; i++) {
      const data = arrayOfData[i] || {};
      for (const key in data) {
        if (!data[key] && requiredFieldName[purpose].includes(key)) {
          fieldName = key;
          row = i + 1;
          break;
        } else {
          row = '';
        }
      }
      if (row) break;
    }

    if (row) {
      message.error(`Field ${fieldName} in row ${row} is empty!`);
    } else if (purpose === 'customer') {
      try {
        let error = false;
        const {
          data: { data = [] },
        } = await CustomerService.getCustomerReport({
          sort_by: 'email asc',
        });
        const registeredCustomersEmail = data.map(({ email = '' }) => email);

        // iteration for validation
        for (let i = 0; i < arrayOfData.length; i++) {
          const { email: csvEmail = '' } = arrayOfData[i] || {};
          const emailIsNotTypo = registeredCustomersEmail.includes(csvEmail);
          if (!emailIsNotTypo) {
            message.error(`Email in row ${i + 1} is not registered`);
            error = true;
            break;
          }
        }

        // hit BE for customer here
        if (!error) {
          const wallets = arrayOfData.map((walletData = {}) => ({
            ...walletData,
            amount: +walletData.amount,
          }));
          const {
            data: { warning = [] },
          } = await CustomerService.bulkTopUp({ wallets });
          afterUpload();
          if (warning.length) {
            warning.forEach((warningMessage) => {
              message.error(warningMessage);
            });
          } else {
            message.success('Successfully bulk top up');
          }
        }
      } catch {
        message.error('Use email and amount as field name');
      }
    } else if (purpose === 'stock') {
      const stockCsvData = arrayOfData.map((stock = {}) => {
        const { acumatica_id = '' } = stock;
        return acumatica_id ? { ...stock } : { ...stock, acumatica_id: null };
      });
      try {
        const {
          data: { warning = [] },
        } = await StockService.bulkUpdateStock({ stocks: stockCsvData });
        if (warning.length) {
          warning.forEach((warningMessage) => {
            message.error(warningMessage);
          });
        } else {
          afterUpload();
          message.success('Successfully upload csv data');
        }
      } catch (error) {
        errHandling(error);
      }
    } else if (purpose === 'rentStock') {
      const stockCsvData = arrayOfData.map((stock = {}) => {
        const { acumatica_id = '' } = stock;
        return acumatica_id ? { ...stock } : { ...stock, acumatica_id: null };
      });
      try {
        const {
          data: { warning = [] },
        } = await RentStockServices.bulkUpdateStock({ stocks: stockCsvData });
        if (warning.length) {
          warning.forEach((warningMessage) => {
            message.error(warningMessage);
          });
        } else {
          afterUpload();
          message.success('Successfully upload csv data');
        }
      } catch (error) {
        errHandling(error);
      }
    } else if (
      ['keyword', 'penalties', 'bulk_create', 'bulk_discount'].includes(purpose)
    ) {
      afterUpload(arrayOfData);
    } else {
      message.error('Csv file is invalid');
    }

    setShouldRemove(true);
  }
  return (
    <div className={noButton ? 'no-button' : 'pd-csv-uploader'}>
      <CSVReader
        config={{
          header,
          skipEmptyLines: 'greedy',
          transformHeader,
        }}
        onUploadAccepted={handleOnFileLoad}>
        {({ getRootProps, getRemoveFileProps }) => (
          <>
            {noButton ? (
              <div {...getRootProps()}>{buttonTitle}</div>
            ) : (
              <PdButton
                className="height-32"
                full
                type={type}
                outline
                {...getRootProps()}>
                {purpose === 'keyword' && <CloudUploadOutlined />}
                {buttonTitle}
              </PdButton>
            )}
            <button
              ref={removeButtonRef}
              {...getRemoveFileProps()}
              style={{ display: 'none' }}
            />
          </>
        )}
      </CSVReader>
    </div>
  );
}
